import { infiniteQueryOptions, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { HistoryHelper } from './HistoryHelper';
import type {
  CashHandHistoryPage,
  CashHistoryFilters,
  CashHandDetails,
  CurrencyOption,
  HistoryGameType,
  HistorySortDirection,
  TournamentHistoryPage,
  TournamentHandHistoryPage,
  TournamentHistoryFilters,
  TournamentLeaderboardEntry,
} from './Type/HistoryTypes';

export const historyQueryKeys = {
  all: ['history'] as const,
  cash: (args: { userId: string; filters?: CashHistoryFilters; pageSize: number; sortBy?: string; sortDirection?: HistorySortDirection }) =>
    [
      ...historyQueryKeys.all,
      'cash',
      { userId: args.userId, filters: args.filters ?? null, pageSize: args.pageSize, sortBy: args.sortBy ?? null, sortDirection: args.sortDirection ?? null },
    ] as const,
  cashDetails: (args: { userId: string; gameId: string }) =>
    [...historyQueryKeys.all, 'cashDetails', { userId: args.userId, gameId: args.gameId }] as const,
  cashCurrencies: (args: { gameType?: HistoryGameType }) =>
    [...historyQueryKeys.all, 'cashCurrencies', { gameType: args.gameType ?? null }] as const,
  tournaments: (args: { userId: string; filters?: TournamentHistoryFilters; pageSize: number; sortBy?: string; sortDirection?: HistorySortDirection }) =>
    [
      ...historyQueryKeys.all,
      'mtt',
      { userId: args.userId, filters: args.filters ?? null, pageSize: args.pageSize, sortBy: args.sortBy ?? null, sortDirection: args.sortDirection ?? null },
    ] as const,
  tournamentHands: (args: { userId: string; tournamentGameId: string; pageSize: number }) =>
    [
      ...historyQueryKeys.all,
      'mttHands',
      { userId: args.userId, tournamentGameId: args.tournamentGameId, pageSize: args.pageSize },
    ] as const,
  tournamentLeaderboard: (tournamentGameId: string) =>
    [...historyQueryKeys.all, 'mttLeaderboard', { tournamentGameId }] as const,
};

const normalizeStatusList = (statusIn?: string[]): string[] | undefined => {
  if (!statusIn || statusIn.length === 0) return undefined;
  const normalized = statusIn.map((status) => status.trim()).filter((status) => status.length > 0);
  if (normalized.length === 0) return undefined;
  return Array.from(new Set(normalized)).sort();
};

const compactFilters = <T extends Record<string, unknown>>(filters?: T): T | undefined => {
  if (!filters) return undefined;
  const entries = Object.entries(filters).filter(([_, value]) => {
    if (value === undefined || value === null) return false;
    if (typeof value === 'string' && value.trim().length === 0) return false;
    if (Array.isArray(value) && value.length === 0) return false;
    return true;
  });
  if (entries.length === 0) return undefined;
  return Object.fromEntries(entries) as T;
};

const normalizeCashFilters = (filters?: CashHistoryFilters): CashHistoryFilters | undefined => {
  return compactFilters(filters);
};

const normalizeTournamentFilters = (filters?: TournamentHistoryFilters): TournamentHistoryFilters | undefined => {
  if (!filters) return undefined;
  const normalized: TournamentHistoryFilters = {
    ...filters,
    statusIn: normalizeStatusList(filters.statusIn),
  };
  return compactFilters(normalized);
};

export const cashHistoryInfiniteQueryOptions = (args: { userId: string; filters?: CashHistoryFilters; pageSize: number; sortBy?: string; sortDirection?: HistorySortDirection }) => {
  const normalizedFilters = normalizeCashFilters(args.filters);
  return infiniteQueryOptions({
    queryKey: historyQueryKeys.cash({ userId: args.userId, filters: normalizedFilters, pageSize: args.pageSize, sortBy: args.sortBy, sortDirection: args.sortDirection }),
    queryFn: ({ pageParam, signal }) =>
      HistoryHelper.getCashHandHistory({
        userId: args.userId,
        filters: normalizedFilters,
        limit: args.pageSize,
        cursor: (pageParam as string | number | undefined) ?? undefined,
        sortBy: args.sortBy,
        sortDirection: args.sortDirection,
        signal,
      }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage: CashHandHistoryPage) => lastPage.nextCursor ?? undefined,
  });
};

export const tournamentHistoryInfiniteQueryOptions = (args: { userId: string; filters?: TournamentHistoryFilters; pageSize: number; sortBy?: string; sortDirection?: HistorySortDirection }) => {
  const normalizedFilters = normalizeTournamentFilters(args.filters);
  return infiniteQueryOptions({
    queryKey: historyQueryKeys.tournaments({ userId: args.userId, filters: normalizedFilters, pageSize: args.pageSize, sortBy: args.sortBy, sortDirection: args.sortDirection }),
    queryFn: ({ pageParam, signal }) =>
      HistoryHelper.getTournamentHistory({
        userId: args.userId,
        filters: normalizedFilters,
        limit: args.pageSize,
        cursor: (pageParam as string | number | undefined) ?? undefined,
        sortBy: args.sortBy,
        sortDirection: args.sortDirection,
        signal,
      }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage: TournamentHistoryPage) => lastPage.nextCursor ?? undefined,
  });
};

export const tournamentHandHistoryInfiniteQueryOptions = (args: { userId: string; tournamentGameId: string; pageSize: number }) =>
  infiniteQueryOptions({
    queryKey: historyQueryKeys.tournamentHands(args),
    queryFn: ({ pageParam, signal }) =>
      HistoryHelper.getTournamentHandHistory({
        userId: args.userId,
        tournamentGameId: args.tournamentGameId,
        limit: args.pageSize,
        cursor: (pageParam as string | number | undefined) ?? undefined,
        signal,
      }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage: TournamentHandHistoryPage) => lastPage.nextCursor ?? undefined,
  });

export function useCashHandHistory(args: {
  userId?: string;
  filters?: CashHistoryFilters;
  pageSize?: number;
  sortBy?: string;
  sortDirection?: HistorySortDirection;
  enabled?: boolean;
}) {
  const userId = args.userId?.trim();
  const pageSize = args.pageSize ?? 30;

  return useInfiniteQuery({
    ...cashHistoryInfiniteQueryOptions({ userId: userId ?? '', filters: args.filters, pageSize, sortBy: args.sortBy, sortDirection: args.sortDirection }),
    enabled: Boolean(userId) && (args.enabled ?? true),
  });
}

export function useTournamentHistory(args: {
  userId?: string;
  filters?: TournamentHistoryFilters;
  pageSize?: number;
  sortBy?: string;
  sortDirection?: HistorySortDirection;
  enabled?: boolean;
}) {
  const userId = args.userId?.trim();
  const pageSize = args.pageSize ?? 30;

  return useInfiniteQuery({
    ...tournamentHistoryInfiniteQueryOptions({ userId: userId ?? '', filters: args.filters, pageSize, sortBy: args.sortBy, sortDirection: args.sortDirection }),
    enabled: Boolean(userId) && (args.enabled ?? true),
  });
}

export function useTournamentHandHistory(args: {
  userId?: string;
  tournamentGameId?: string;
  pageSize?: number;
  enabled?: boolean;
}) {
  const userId = args.userId?.trim();
  const tournamentGameId = args.tournamentGameId?.trim();
  const pageSize = args.pageSize ?? 50;

  return useInfiniteQuery({
    ...tournamentHandHistoryInfiniteQueryOptions({
      userId: userId ?? '',
      tournamentGameId: tournamentGameId ?? '',
      pageSize,
    }),
    enabled: Boolean(userId) && Boolean(tournamentGameId) && (args.enabled ?? true),
  });
}

export function useCashHandDetails(args: {
  userId?: string;
  gameId?: string;
  enabled?: boolean;
}) {
  const userId = args.userId?.trim();
  const gameId = args.gameId?.trim();

  return useQuery<CashHandDetails>({
    queryKey: historyQueryKeys.cashDetails({ userId: userId ?? '', gameId: gameId ?? '' }),
    queryFn: ({ signal }) =>
      HistoryHelper.getCashHandDetails({
        userId: userId ?? '',
        gameId: gameId ?? '',
        signal,
      }),
    enabled: Boolean(userId) && Boolean(gameId) && (args.enabled ?? true),
  });
}

export function useCashCurrencyOptions(args: {
  gameType?: HistoryGameType;
  enabled?: boolean;
}) {
  return useQuery<CurrencyOption[]>({
    queryKey: historyQueryKeys.cashCurrencies({ gameType: args.gameType }),
    queryFn: ({ signal }) =>
      HistoryHelper.getCashCurrencyOptions({
        gameType: args.gameType,
        signal,
      }),
    enabled: args.enabled ?? true,
  });
}

export function useTournamentLeaderboard(args: {
  tournamentGameId?: string;
  enabled?: boolean;
}) {
  const id = args.tournamentGameId?.trim();

  return useQuery<TournamentLeaderboardEntry[]>({
    queryKey: historyQueryKeys.tournamentLeaderboard(id ?? ''),
    queryFn: ({ signal }) =>
      HistoryHelper.getTournamentLeaderboard({
        tournamentGameId: id ?? '',
        signal,
      }),
    enabled: Boolean(id) && (args.enabled ?? true),
  });
}
